import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Zap } from 'lucide-react';
import AuthPrompt from './AuthPrompt';

/**
 * PaidFeatureGate - Wraps paid feature pages to enforce plan access
 * 
 * Props:
 * - feature: string (e.g., 'save_deals', 'compare_deals', 'deal_inbox')
 * - featureDisplayName: string (human-readable name for the feature)
 * - user: user object from UserContext
 * - lang: 'en' | 'es'
 * - authIcon: React component for auth prompt icon
 * - authTitle: string for auth prompt
 * - authDescription: string for auth prompt
 * - redirectAfterAuth: string (URL to redirect after auth)
 * - children: page content to render if allowed
 */
const PaidFeatureGate = ({ 
  feature,
  featureDisplayName,
  user,
  lang = 'en',
  authIcon,
  authTitle,
  authDescription,
  redirectAfterAuth,
  children
}) => {
  const navigate = useNavigate();
  
  // Check if user can access this feature
  const canAccess = user?.is_founder || user?.features?.[feature] === true;
  
  // BRANCH 1: Not logged in - show AuthPrompt (sign in)
  if (!user) {
    return (
      <div className="container mx-auto px-4 pt-24 pb-12">
        <AuthPrompt
          icon={authIcon}
          title={authTitle}
          description={authDescription}
          lang={lang}
          redirectAfterAuth={redirectAfterAuth}
        />
      </div>
    );
  }
  
  // BRANCH 2: Logged in but NOT allowed (Free user) - show upgrade prompt
  if (!canAccess) {
    return (
      <div className="container mx-auto px-4 pt-16 pb-12">
        <div className="max-w-lg mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
            data-testid="gate-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'en' ? 'Back' : 'Regresar'}
          </button>
          
          {/* Upgrade Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {lang === 'en' ? 'Upgrade Required' : 'Actualización Requerida'}
              </h2>
              <p className="text-blue-100 text-sm max-w-sm mx-auto">
                {lang === 'en' 
                  ? `${featureDisplayName} is available on Investor and Pro plans.`
                  : `${featureDisplayName} está disponible en los planes Investor y Pro.`}
              </p>
            </div>
            
            {/* Benefits */}
            <div className="px-6 py-6">
              <p className="text-sm text-slate-600 mb-4 text-center">
                {lang === 'en' 
                  ? 'Upgrade to unlock premium features:'
                  : 'Actualiza para desbloquear funciones premium:'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Zap className="w-4 h-4 text-blue-600" />
                  {lang === 'en' ? 'Save and organize deals' : 'Guardar y organizar tratos'}
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Zap className="w-4 h-4 text-blue-600" />
                  {lang === 'en' ? 'Compare multiple properties' : 'Comparar múltiples propiedades'}
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Zap className="w-4 h-4 text-blue-600" />
                  {lang === 'en' ? 'Deal Inbox for submissions' : 'Buzón de tratos para envíos'}
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-700">
                  <Zap className="w-4 h-4 text-blue-600" />
                  {lang === 'en' ? 'Share and email reports' : 'Compartir y enviar reportes'}
                </li>
              </ul>
              
              {/* CTA Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-md"
                  data-testid="gate-view-plans-btn"
                >
                  {lang === 'en' ? 'View Plans' : 'Ver Planes'}
                </button>
                <button
                  onClick={() => navigate('/analyze')}
                  className="w-full px-6 py-3 bg-white text-slate-700 border border-slate-300 rounded-lg font-medium hover:bg-slate-50 transition-all"
                  data-testid="gate-analyze-btn"
                >
                  {lang === 'en' ? 'Continue Analyzing (Free)' : 'Continuar Analizando (Gratis)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // BRANCH 3: Allowed (Founder/Investor/Pro) - render children
  return children;
};

export default PaidFeatureGate;
